import React, { useEffect } from 'react';
import { DetailBuilder } from '@eigen3m/react-base-frontend';
import { Form, Image } from 'antd';
import parse from 'html-react-parser';
import './styles.less';
import {
  transformerGetDataInformation,
  transformerGetDataParticipant,
  transformerGetDataPaymentAndPricing,
  transformerGetDataRegistration,
  transformerGetDataSchedules,
} from '@admin-app/pages/events/event-data/domain/transformer/transformer.get';
import { ScheduleList } from './schedule-list';
import produce from 'immer';
import { SpeakerList } from './speaker-list';
import { RegistrationList } from './registration-list';
import PaymentAndPricings from './payment-and-pricings';
import { Maps } from '@components/maps';

interface Props {
  value?: any;
  onChange?: (value?: any) => void;
  showName?: boolean;
  showBanner?: boolean;
}

export function EventActivitiesDetail(props: Props) {
  const { value, showName = false, showBanner = false } = props;
  const [form] = Form.useForm();

  useEffect(() => {
    if (value) {
      const newValue = {
        ...value,
        information: transformerGetDataInformation(value?.information),
        participants: transformerGetDataParticipant(value?.participants),
        schedules: transformerGetDataSchedules(value?.schedules),
        registrations: transformerGetDataRegistration(value?.registrations),
        payment_pricings: transformerGetDataPaymentAndPricing(value?.payment_pricings),
      };
      form.setFieldsValue(newValue);
    }
  }, [value]);

  return (
    <div>
      <Form form={form} layout="vertical">
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const description = getFieldValue(['information', 'description']);
            const termsAndConditions = getFieldValue(['information', 'term_and_conditions']);
            const speakers = [];
            const days = getFieldValue(['schedules', 'days']);
            const dataSpeakers = produce(speakers, (draft) => {
              days?.forEach((day: any) => {
                if (!!day?.speakers?.length) draft.push(...day?.speakers);
                if (!!day?.sessions?.length) {
                  day?.sessions?.forEach((session: any) => {
                    if (!!session?.speakers?.length) draft.push(...session?.speakers);
                    if (!!session?.workshops?.length) {
                      session?.workshops?.forEach((workshop: any) => {
                        if (!!workshop?.speakers?.length) draft?.push(...workshop?.speakers);
                      });
                    }
                  });
                }
              });
            });

            const uniqueSpeakers = dataSpeakers?.filter(
              (x, index, self) =>
                index === self.findIndex((y) => y?.name === x?.name && y?.description === x?.description),
            );

            const venueName = getFieldValue(['information', 'venue', 'name']);
            return (
              <DetailBuilder
                columns={[
                  {
                    fields: [
                      {
                        gridColumn: { span: 24 },
                        label: <h3>Event Name</h3>,
                        name: ['information', 'name'],
                        fieldType: 'text',
                        renderTextProps: {
                          renderValue(value) {
                            return value ?? '-';
                          },
                          defaultRender() {
                            return '-';
                          },
                        },
                        customHidden() {
                          return !showName;
                        },
                      },
                      {
                        gridColumn: { span: 24 },
                        renderField(form) {
                          const documentUrl = form.getFieldValue(['information', 'banner_url']);
                          const assetUrl = process.env.REACT_APP_BASE_ASSET_URL;
                          const imageUrl = assetUrl && documentUrl ? assetUrl + documentUrl : 'error';
                          return (
                            <Form.Item label={<h3>Banner</h3>}>
                              {documentUrl ? (
                                <Image
                                  width={documentUrl !== 'error' ? 'auto' : 200}
                                  height={150}
                                  src={imageUrl}
                                  fallback="data:image/png;base64,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"
                                />
                              ) : (
                                '-'
                              )}
                            </Form.Item>
                          );
                        },
                        customHidden() {
                          return !showBanner;
                        },
                      },
                      {
                        renderField() {
                          return (
                            <Form.Item label={<h3>Description</h3>}>{description ? parse(description) : '-'}</Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                      },
                      {
                        gridColumn: { span: 24 },
                        label: <h3>Event Type</h3>,
                        name: ['information', 'event_type'],
                        fieldType: 'text',
                        renderTextProps: {
                          renderValue(value) {
                            return value ?? '-';
                          },
                          defaultRender() {
                            return '-';
                          },
                        },
                      },
                      {
                        renderField(form) {
                          const addressDescription = form?.getFieldValue([
                            'information',
                            'venue',
                            'address_description',
                          ]);
                          return (
                            <Form.Item label={<h3>Location</h3>}>
                              <h4 style={{ color: '#777' }}>Venue</h4>
                              <div style={{ display: 'block', marginBottom: '20px' }}>{venueName}</div>
                              <Form.Item name={['information', 'venue', 'address']} noStyle>
                                <Maps width="100%" addressDescription={addressDescription} />
                              </Form.Item>
                            </Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                        customHidden(form) {
                          const eventType = form.getFieldValue(['information', 'event_type'])?.toLowerCase();
                          return eventType === 'on site' ? false : true;
                        },
                      },
                      {
                        renderField() {
                          return (
                            <Form.Item label={<h3>Speaker</h3>}>
                              <SpeakerList value={uniqueSpeakers} />
                            </Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                      },
                      {
                        renderField() {
                          return (
                            <Form.Item label={<h3>Schedule</h3>} name={['schedules']}>
                              <ScheduleList />
                            </Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                      },
                      {
                        renderField() {
                          return (
                            <Form.Item label={<h3>Participant</h3>} shouldUpdate>
                              {(form) => {
                                const description = form.getFieldValue(['participants', 'description']);
                                const ageMin = form.getFieldValue(['participants', 'age_min']);
                                const ageMax = form.getFieldValue(['participants', 'age_max']);
                                const gender = form.getFieldValue(['participants', 'gender']);
                                const maritalStatus = form.getFieldValue(['participants', 'marital_status']);
                                return (
                                  <>
                                    {!!description && <p>{description}</p>}
                                    {(!!ageMin || !!ageMax) && (
                                      <Form.Item label="Age">
                                        {ageMin && ageMax
                                          ? `${ageMin} - ${ageMax} years old`
                                          : ageMin
                                          ? `above ${ageMin} years old`
                                          : ageMax
                                          ? `below ${ageMax} years old`
                                          : '-'}
                                      </Form.Item>
                                    )}
                                    {!!gender && <Form.Item label="Gender">{gender ?? '-'}</Form.Item>}
                                    {!!maritalStatus && (
                                      <Form.Item label="Marital Status">{maritalStatus ?? '-'}</Form.Item>
                                    )}
                                  </>
                                );
                              }}
                            </Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                      },
                      {
                        renderField() {
                          return (
                            <Form.Item label={<h3>Registration</h3>} name={['registrations']}>
                              <RegistrationList />
                            </Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                      },
                      {
                        renderField() {
                          return (
                            <Form.Item label={<h3>Payment & Pricings</h3>} name={['payment_pricings']}>
                              <PaymentAndPricings />
                            </Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                      },
                      {
                        gridColumn: { span: 24 },
                        label: <h3>Event Link</h3>,
                        name: ['information', 'event_link'],
                        fieldType: 'text',
                        renderTextProps: {
                          renderValue(value) {
                            return value ?? '-';
                          },
                          defaultRender() {
                            return 'Link will be sent to you after we confirm your payment. Follow the instruction stated in the email.';
                          },
                        },
                        customHidden(form) {
                          const eventType = form.getFieldValue(['information', 'event_type'])?.toLowerCase();
                          return eventType === 'online' ? false : true;
                        },
                      },
                      {
                        renderField() {
                          return (
                            <Form.Item label={<h3>Terms & Conditions</h3>}>
                              {termsAndConditions ? parse(termsAndConditions) : '-'}
                            </Form.Item>
                          );
                        },
                        gridColumn: { span: 24 },
                      },
                    ],
                  },
                ]}
              />
            );
          }}
        </Form.Item>
      </Form>
    </div>
  );
}
